import { useEffect, useState } from 'react';

import { ArrowBack, Search } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControl, Grid, IconButton, Typography } from '@mui/material';
import { includes, isEqual } from '@npi/buddy-utils';
import { useFormik } from 'formik';
import { CompanyLookupResponse, CompanyReportResponse } from 'types/graphql';

import CloseIcon from 'src/assets/images/icons/CloseButtonSVG';
import { useCompanyReport } from 'src/hooks';

import CompanyList from './components/CompanyList';
import { DirectorsCheckItem } from './components/DirectorsCheckItem';

const initialSelectedValues = {
    companyAddress: '',
    companyIncorporationDate: null,
    companyName: '',
    pi_BusinessType: '',
    directors: [],
    registrationNumber: '',
    websiteURL: '',
};

type CompanyLookUpListProps = {
    data: CompanyLookupResponse[];
    onClose: () => void;
    onSelectCompanyReport: (companyReport: CompanyReportResponse) => void;
    setFieldValue?: (name: string, value: any) => void;
};

export const CompanyLookUpList = ({
    data,
    onClose,
    onSelectCompanyReport,
    setFieldValue,
}: CompanyLookUpListProps) => {
    const {
        isLoadingCompanyReport,
        companyReport: selected,
        findCompanyReport,
    } = useCompanyReport();

    const [selectAll, setSelectAll] = useState(false);
    const [selectedCompanyNumber, setSelectedCompanyNumber] = useState(null);

    const selectedDirectors = selected?.directors ?? [];
    const shareHolderData = selectedDirectors
        .map((director) => {
            return director.numberOfShares;
        })
        .filter((numberOfShares) => {
            return numberOfShares !== null && numberOfShares > 0;
        });

    const hasBeneficialOwners = shareHolderData?.some((numberOfShares) => {
        return numberOfShares > 0;
    });

    const formik = useFormik({
        initialValues: {
            companyName: selected?.companyName ?? '',
            registrationNumber: selected?.registrationNumber,
            pi_BusinessType: selected?.pi_BusinessType ?? '',
            websiteURL: selected?.websiteURL ?? '',
            companyAddress: selected?.companyAddress ?? '',
            directors: selectedDirectors,
            companyIncorporationDate: selected?.companyIncorporationDate,
        },
        onSubmit: (values: CompanyReportResponse) => {
            onSelectCompanyReport(values);
        },
    });

    useEffect(() => {
        if (selected?.shareholderReport?.b1DocumentId && setFieldValue) {
            setFieldValue('b1FormId', selected?.shareholderReport?.b1DocumentId ?? null);
        }
    }, [selected, setFieldValue]);

    const handleValueSelect = (key, value) => {
        const newObject = { ...formik.values };

        if (key === 'directors') {
            const hasDirector = includes(newObject.directors, value);

            if (hasDirector) {
                newObject.directors = newObject.directors.filter((director) => {
                    return !isEqual(director, value);
                });
            } else {
                newObject.directors = [...newObject.directors, value];
            }
        } else if (newObject[key] === value) {
            newObject[key] = '';
        } else if (newObject[key] !== value) {
            newObject[key] = value;
        }

        const allDeselected = Object.keys(newObject).every((k) => {
            return k !== 'selectAll' && newObject[k] === '';
        });

        setSelectAll(!allDeselected);

        formik.setValues(newObject);
    };

    const backArrowStyle = {
        backgroundColor: 'white',
        width: '100%',
        // Add any other inline styles you need
        padding: '10px', // Optional: Add padding for spacing
    };

    return (
        <Box
            maxWidth="90%"
            margin="20px auto"
            height="90vh"
            sx={{ overflowY: 'scroll', borderRadius: '16px' }}
        >
            {!selectedCompanyNumber || isLoadingCompanyReport ? (
                <CompanyList
                    selectedCompanyNumber={selectedCompanyNumber}
                    isFetchingReport={isLoadingCompanyReport}
                    onSelectCompany={async (el: CompanyLookupResponse) => {
                        setSelectedCompanyNumber(el.registrationNumber);
                        await findCompanyReport(el.registrationNumber);
                    }}
                    data={data}
                    onClose={onClose}
                />
            ) : (
                <>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            background: `linear-gradient(90deg, #FF8008 0%, #FFC837 100%)`,
                            padding: '24px',
                        }}
                    >
                        <Box display="flex" alignItems="center" color="#FFFAE5">
                            <Search color="inherit" />
                            <Typography fontSize="24px" color="#FFFAE5" marginLeft="14px">
                                Results
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} data-testid={'company-list-close'}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <div style={backArrowStyle}>
                        <Button
                            onClick={() => {
                                return setSelectedCompanyNumber(null);
                            }}
                        >
                            <ArrowBack
                                sx={{ color: '#F18F34' }}
                                fontSize="large"
                                style={{ marginRight: 8 }}
                            />{' '}
                            Back to Search
                        </Button>
                    </div>

                    <form
                        onSubmit={() => {
                            return formik.handleSubmit();
                        }}
                    >
                        <Box
                            bgcolor="white"
                            display="flex"
                            flexDirection="column"
                            sx={{
                                borderBottomLeftRadius: '16px',
                                borderBottomRightRadius: '16px',
                                padding: '24px',
                            }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                marginTop="40px"
                            >
                                <Typography fontSize="24px" lineHeight="34px" color="#434341">
                                    {selected?.companyName}
                                </Typography>
                                <Box display="flex" alignItems="center">
                                    <Checkbox
                                        data-testid={'company-list-select-all-checkbox'}
                                        sx={{ borderColor: '#F18F34' }}
                                        onChange={({ target }) => {
                                            const { checked } = target;
                                            setSelectAll(checked);

                                            if (checked) {
                                                formik.setValues(selected);
                                            } else {
                                                formik.setValues(initialSelectedValues);
                                            }
                                        }}
                                    />
                                    Select All
                                </Box>
                            </Box>

                            <Box height="1px" width="100%" bgcolor="#F18F34" marginTop="8px" />
                            <Typography
                                fontSize="20px"
                                color="#1D1D1B"
                                lineHeight="30px"
                                marginY="24px"
                                marginLeft="10px"
                            >
                                Details
                            </Typography>
                            <Grid container spacing={35} columns={2} gridColumn={2}>
                                <Grid item width="50%" alignItems="center">
                                    <Box marginRight="40px">
                                        <Box display="flex" alignItems="center">
                                            <FormControl
                                                onClick={() => {
                                                    handleValueSelect(
                                                        'companyName',
                                                        selected?.companyName
                                                    );
                                                }}
                                            >
                                                <Checkbox
                                                    sx={{ color: '#F18F34' }}
                                                    checked={
                                                        formik.values.companyName ===
                                                        selected?.companyName
                                                    }
                                                />
                                            </FormControl>
                                            <Typography fontWeight="bold">Company Name</Typography>
                                        </Box>
                                        <Typography style={{ marginLeft: '44px' }}>
                                            {selected?.companyName}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item width="50%">
                                    <Box>
                                        <Box display="flex" alignItems="center">
                                            <FormControl
                                                onClick={() => {
                                                    handleValueSelect(
                                                        'registrationNumber',
                                                        selected?.registrationNumber
                                                    );
                                                }}
                                            >
                                                <Checkbox
                                                    sx={{ color: '#F18F34' }}
                                                    checked={
                                                        formik.values.registrationNumber ===
                                                        selected?.registrationNumber
                                                    }
                                                />
                                            </FormControl>
                                            <Typography fontWeight="bold">
                                                Registration No.
                                            </Typography>
                                        </Box>
                                        <Typography style={{ marginLeft: '44px' }}>
                                            {selected?.registrationNumber}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Box display="flex" alignItems="center">
                                            <FormControl
                                                onClick={() => {
                                                    handleValueSelect(
                                                        'websiteURL',
                                                        selected?.websiteURL
                                                    );
                                                }}
                                            >
                                                <Checkbox
                                                    sx={{ color: '#F18F34' }}
                                                    checked={
                                                        formik.values.websiteURL ===
                                                        selected?.websiteURL
                                                    }
                                                />
                                            </FormControl>
                                            <Typography fontWeight="bold">Website</Typography>
                                        </Box>
                                        <Typography style={{ marginLeft: '44px' }}>
                                            {selected?.websiteURL}
                                        </Typography>
                                    </Box>

                                    <Box marginLeft="44px" marginTop="15px">
                                        <Typography
                                            color="#1D1D1B"
                                            lineHeight="30px"
                                            fontWeight="bold"
                                        >
                                            Address
                                        </Typography>
                                        <Typography>{selected?.companyAddress}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Typography
                                fontSize="20px"
                                color="#1D1D1B"
                                lineHeight="30px"
                                marginY="24px"
                                marginLeft="10px"
                            >
                                Address
                            </Typography>
                            <Grid container spacing={2} columns={2} gridColumn={2}>
                                <Grid item width="50%">
                                    <Box>
                                        <Box display="flex" alignItems="center">
                                            <FormControl
                                                onClick={() => {
                                                    handleValueSelect(
                                                        'companyAddress',
                                                        selected?.companyAddress
                                                    );
                                                }}
                                            >
                                                <Checkbox
                                                    sx={{ color: '#F18F34' }}
                                                    checked={
                                                        formik.values.companyAddress ===
                                                        selected?.companyAddress
                                                    }
                                                />
                                            </FormControl>
                                            <Typography fontWeight="bold">
                                                Company Address
                                            </Typography>
                                        </Box>
                                        <Typography style={{ marginLeft: '44px' }}>
                                            {selected?.companyAddress}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box display="flex" flexDirection="column" gap="20px" marginTop="30px">
                                <Box>
                                    <Typography
                                        marginLeft="10px"
                                        fontSize="20px"
                                        color="#1D1D1B"
                                        lineHeight="30px"
                                    >
                                        Beneficial Owners
                                    </Typography>
                                    <Grid container spacing={2} columns={2} gridColumn={2}>
                                        {(shareHolderData || []).map((numberOfShares, index) => {
                                            if (numberOfShares > 0) {
                                                const beneficialOwner = selectedDirectors[index];
                                                return (
                                                    <Grid item key={index}>
                                                        <DirectorsCheckItem
                                                            director={`${beneficialOwner?.firstName} ${beneficialOwner?.lastName}`}
                                                            checked={includes(
                                                                formik.values.directors,
                                                                beneficialOwner
                                                            )}
                                                            onClick={() => {
                                                                return handleValueSelect(
                                                                    'directors',
                                                                    beneficialOwner
                                                                );
                                                            }}
                                                        />
                                                    </Grid>
                                                );
                                            }
                                            return null;
                                        })}
                                        {!hasBeneficialOwners && (
                                            <Grid item>
                                                <Typography
                                                    style={{
                                                        marginTop: '20px',
                                                        marginLeft: '11px',
                                                        color: '#A9A9A9',
                                                        fontSize: '16px',
                                                    }}
                                                >
                                                    No beneficial owners
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>

                                <Box>
                                    <Typography
                                        marginLeft="10px"
                                        fontSize="20px"
                                        color="#1D1D1B"
                                        lineHeight="30px"
                                    >
                                        Directors
                                    </Typography>
                                    <Grid container spacing={2} columns={2} gridColumn={2}>
                                        {selectedDirectors?.length > 0 ? (
                                            selectedDirectors.map((selectedDirector) => {
                                                return (
                                                    <Grid item key={selectedDirector.id}>
                                                        <DirectorsCheckItem
                                                            director={`${selectedDirector?.firstName} ${selectedDirector?.lastName}`}
                                                            checked={includes(
                                                                formik.values.directors,
                                                                selectedDirector
                                                            )}
                                                            onClick={() => {
                                                                return handleValueSelect(
                                                                    'directors',
                                                                    selectedDirector
                                                                );
                                                            }}
                                                        />
                                                    </Grid>
                                                );
                                            })
                                        ) : (
                                            <Grid item>
                                                <Typography
                                                    style={{
                                                        marginTop: '20px',
                                                        marginLeft: '11px',
                                                        color: '#A9A9A9',
                                                        fontSize: '16px',
                                                    }}
                                                >
                                                    No directors
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Box>

                            <Button
                                sx={{
                                    color: 'white',
                                    bgcolor: '#F39E4E',
                                    alignSelf: 'flex-end',
                                    marginTop: '40px',
                                    ':hover': { backgroundColor: '#F39E4E', opacity: '.8' },
                                }}
                                onClick={() => {
                                    return formik.handleSubmit();
                                }}
                            >
                                Use Selected Details
                            </Button>
                        </Box>
                    </form>
                </>
            )}
        </Box>
    );
};
